/**
 * EditorJS inline tool that wraps the current selection in
 * `<span style="font-size:Npt">…</span>`.
 *
 * Pattern matches the existing ColorTool. The size-picker UI is portalled
 * via `createFloatingPalette` (see that module for why: EditorJS's own
 * popover clips a nested absolutely-positioned child and reentrantly
 * re-toggles it on every click inside its subtree).
 *
 * The pure font-size-application logic + presets live in
 * `apps/web/src/lib/text-font-size-ops.ts` so the React `useSelectionToolbar`
 * (used on container titles) can share the exact same output shape.
 */
import { BrandColors } from '@/lib/brand-colors';
import { SPAN_SANITIZE } from '@/editorjs-tools/sanitize-config';
import { createFloatingPalette, createPaletteGestureHandlers, applyFloatingPaletteChrome, type FloatingPalette, type PaletteGestureHandlers } from '@/editorjs-tools/floating-palette';
import {
  DEFAULT_FONT_SIZE_PALETTE,
  applyFontSizeToRange,
  getLastPickedFontSize,
  getSelectionFontSize,
  isCaretInsideFontSizedSpan,
  removeFontSizeFromRange,
} from '@/lib/text-font-size-ops';

const DEFAULT_LABEL = 'A';

export class FontSizeTool {
  static isInline = true as const;
  static sanitize = { span: SPAN_SANITIZE };
  static title = 'Font size';

  private wrapper: HTMLElement | null = null;
  private label: HTMLElement | null = null;
  private currentRange: Range | null = null;
  private palette: FloatingPalette | null = null;
  private paletteGesture: PaletteGestureHandlers | null = null;

  render(): HTMLElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.classList.add('ce-inline-tool', 'ce-inline-tool--font-size');
    button.title = 'Font size';
    const label = document.createElement('span');
    label.setAttribute(
      'style',
      'display:inline-block;min-width:14px;font-size:11px;font-weight:700;text-align:center',
    );
    label.textContent = DEFAULT_LABEL;
    button.appendChild(label);
    this.wrapper = button;
    this.label = label;
    this.paletteGesture = createPaletteGestureHandlers(
      () => this.getOrCreatePalette(),
    );
    button.addEventListener('mousedown', e => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
        this.currentRange = sel.getRangeAt(0).cloneRange();
      }
      this.paletteGesture?.onToolButtonMouseDown(e);
    });
    return button;
  }

  surround(range: Range): void {
    this.currentRange = range.cloneRange();
    this.paletteGesture?.onSurroundPalette();
  }

  clear(): void {
    // See ColorTool.clear() — keep the portalled palette alive after the
    // inline toolbar tears down its tool instances.
    this.palette = null;
    this.paletteGesture?.resetGestureState();
  }

  // EditorJS calls checkState() on every selection change inside the
  // editor. We piggy-back to keep the label in sync with whatever size
  // is currently at the caret (or, failing that, the last-picked size).
  checkState(): boolean {
    this.updateLabel();
    return isCaretInsideFontSizedSpan();
  }

  /** Public for testing. Wraps the range. Returns false on invalid size. */
  applyFontSize(range: Range, pt: number): boolean {
    const ok = applyFontSizeToRange(range, pt);
    if (ok) {
      this.updateLabel();
      this.refreshRangeFromLiveSelection();
    }
    return ok;
  }

  private updateLabel(): void {
    if (!this.label) return;
    const active = getSelectionFontSize() ?? getLastPickedFontSize();
    this.label.textContent = active ? String(active) : DEFAULT_LABEL;
  }

  // `applyFontSizeToRange`/`removeFontSizeFromRange` collapse whatever range
  // they're called on (`Range#extractContents` side effect) and reselect the
  // mutated contents as the live browser selection. Re-derive our cached
  // range from that live selection so the NEXT chip/reset click in the same
  // palette session acts on the correct (non-collapsed) range instead of a
  // stale, now-empty one.
  private refreshRangeFromLiveSelection(): void {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      this.currentRange = sel.getRangeAt(0);
    }
  }

  private getOrCreatePalette(): FloatingPalette {
    if (this.palette) return this.palette;
    const anchor = this.wrapper;
    if (!anchor) throw new Error('FontSizeTool.render() must run before surround()');
    const palette = createFloatingPalette(anchor);
    applyFloatingPaletteChrome(palette.element);

    // Row 1 — preset sizes.
    const sizeRow = document.createElement('div');
    sizeRow.setAttribute('style', 'display:flex;gap:4px;flex-wrap:wrap;max-width:180px');
    for (const pt of DEFAULT_FONT_SIZE_PALETTE) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.setAttribute('aria-label', `Apply font size ${pt}pt`);
      chip.title = `${pt}pt`;
      chip.textContent = String(pt);
      chip.setAttribute(
        'style',
        'min-width:24px;height:24px;padding:0 4px;border:1px solid #c8ccd2;border-radius:4px;background:white;cursor:pointer;font-size:12px',
      );
      chip.addEventListener('mousedown', e => {
        e.preventDefault();
        if (this.currentRange) this.applyFontSize(this.currentRange, pt);
        // Palette stays open — explicit Confirm below closes it.
      });
      sizeRow.appendChild(chip);
    }
    palette.element.appendChild(sizeRow);

    // Row 2 — actions (reset + confirm).
    const actionsRow = document.createElement('div');
    actionsRow.setAttribute(
      'style',
      'display:flex;gap:6px;align-items:center;border-top:1px solid #eef0f3;padding-top:6px',
    );

    const resetBtn = document.createElement('button');
    resetBtn.type = 'button';
    resetBtn.setAttribute('aria-label', 'Reset font size');
    resetBtn.title = 'Reset font size';
    resetBtn.setAttribute(
      'style',
      'width:24px;height:24px;border:1px solid #c8ccd2;border-radius:4px;background:white;color:#1d2128;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0',
    );
    resetBtn.textContent = '⌀';
    resetBtn.addEventListener('mousedown', e => {
      e.preventDefault();
      const range = this.currentRange;
      if (range) {
        const removed = removeFontSizeFromRange(range);
        if (removed) {
          this.updateLabel();
          this.refreshRangeFromLiveSelection();
        }
      }
      // Palette stays open.
    });
    actionsRow.appendChild(resetBtn);

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.setAttribute('aria-label', 'Confirm font size selection');
    confirmBtn.title = 'Confirm';
    confirmBtn.setAttribute(
      'style',
      `margin-left:auto;width:24px;height:24px;border:1px solid ${BrandColors.purple[950]};border-radius:4px;background:${BrandColors.purple[950]};color:white;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0;font-weight:700`,
    );
    confirmBtn.textContent = '✓';
    confirmBtn.addEventListener('mousedown', e => {
      e.preventDefault();
      palette.close();
    });
    actionsRow.appendChild(confirmBtn);

    palette.element.appendChild(actionsRow);

    this.palette = palette;
    return palette;
  }
}
